// ─── User & Auth ────────────────────────────────────────────────────────────

export interface User {
  uid: string;
  email: string | null;
  displayName: string | null;
  plan: 'free' | 'premium' | 'homeschool' | 'family';
  planExpiresAt: Date | null;
  createdAt: Date;
  language: 'no' | 'en';
}

// ─── Child ───────────────────────────────────────────────────────────────────

export type AgeBand = '4-7' | '8-12' | '13-16';
export type Interest = 'science' | 'making' | 'numbers' | 'stories' | 'nature' | 'technology';

// ─── Phase (Sagan Curriculum Engine) ────────────────────────────────────────
// Four developmental phases that map age → cognitive stage → lesson style.
// Synthesised from NCERT, Norwegian LK20, Finnish POPS, and US Common Core.

export type Phase = 'spark' | 'explorer' | 'builder' | 'challenger';

export const PHASE_CONFIG: Record<Phase, {
  label: string;
  labelNo: string;
  emoji: string;
  ageRange: string;
  color: string;
  bg: string;
  border: string;
  description: string;
  descriptionNo: string;
}> = {
  spark: {
    label: 'Spark',
    labelNo: 'Gnist',
    emoji: '✨',
    ageRange: '4–6',
    color: '#956007',
    bg: '#FEF3C7',
    border: '#FDE68A',
    description: 'Wonder through play, story and senses',
    descriptionNo: 'Undring gjennom lek, fortelling og sansning',
  },
  explorer: {
    label: 'Explorer',
    labelNo: 'Utforsker',
    emoji: '🔭',
    ageRange: '7–9',
    color: '#0B8059',
    bg: '#DCFCE7',
    border: '#6EE7B7',
    description: 'Discover through experiment and story',
    descriptionNo: 'Oppdagelse gjennom eksperiment og fortelling',
  },
  builder: {
    label: 'Builder',
    labelNo: 'Bygger',
    emoji: '🔧',
    ageRange: '10–12',
    color: '#2D64BD',
    bg: '#DBEAFE',
    border: '#93C5FD',
    description: 'Create through projects and reasoning',
    descriptionNo: 'Skaping gjennom prosjekter og resonnering',
  },
  challenger: {
    label: 'Challenger',
    labelNo: 'Utfordrer',
    emoji: '🚀',
    ageRange: '13–16',
    color: '#764ED1',
    bg: '#EDE9FE',
    border: '#C4B5FD',
    description: 'Inquiry, abstraction and real-world impact',
    descriptionNo: 'Utforsking, abstraksjon og virkelig innvirkning',
  },
};

export function getPhase(age: number): Phase {
  if (age <= 6)  return 'spark';
  if (age <= 9)  return 'explorer';
  if (age <= 12) return 'builder';
  return 'challenger';
}

export interface Child {
  id: string;
  name: string;
  age: number;
  ageBand: AgeBand;
  phase: Phase;
  languagePreference: 'no' | 'en';
  activePathwayId: string | null;
  interests: Interest[];
  avatar: string;
  /** The child's favorite mascot subject — derived from their first selected interest */
  favoriteMascot?: string;
  createdAt: Date;
  lastActiveAt: Date;
  /** GDPR Art. 8 — timestamp when the parent/guardian gave consent for data processing */
  consentGivenAt: Date;
}

// ─── Pathway ─────────────────────────────────────────────────────────────────

export type Subject = 'mathematics' | 'science' | 'technology' | 'engineering' | 'arts' | 'futureskills';

export interface Pathway {
  id: string;
  title: string;
  titleEn?: string;
  description: string;
  descriptionEn?: string;
  subject: Subject;
  strand: string;
  ageBand: AgeBand;
  phase: Phase;
  level: 1 | 2 | 3;
  lessonIds: string[];
  totalLessons: number;
  estimatedWeeks: number;
  isPrebuilt: boolean;
  generatedForChildId: string | null;
  createdAt: Date;
}

// ─── Progress ────────────────────────────────────────────────────────────────

export interface Progress {
  pathwayId: string;
  lessonsCompleted: string[];
  currentLessonId: string;
  startedAt: Date;
  lastActiveAt: Date;
  streakDays: number;
  totalMinutes: number;
  /** YYYY-MM-DD → minutes learned that day — used for the 7-day activity chart */
  weeklyActivity?: Record<string, number>;
}

// ─── Lesson ──────────────────────────────────────────────────────────────────

export interface SaganHook {
  type: 'question' | 'observation' | 'number' | 'image_description';
  content: string;
}

export interface RealWorldBridge {
  context: string;
  content: string;
}

export interface WorkedExample {
  problem: string;
  solution: string;
  explanation: string;
}

export interface TerminologyItem {
  term: string;
  definition: string;
}

export interface CoreConcept {
  definition: string;
  terminology: string[] | TerminologyItem[];
  explanation: string;
  workedExamples: WorkedExample[];
}

export type InteractiveType = 'slider' | 'drag_drop' | 'fill_blank' | 'number_input' | 'phet_embed' | 'drawing' | 'phet' | 'custom' | 'video' | 'desmos';

export interface InteractiveComponent {
  type: InteractiveType;
  description: string;
  phetUrl: string | null;
}

export interface PracticeQuestion {
  id?: string;
  question: string;
  type: 'multiple_choice' | 'short_answer' | 'calculation' | 'draw';
  options?: string[] | null;
  correctAnswer: string;
  explanation: string;
  solutionSteps?: string[];   // step-by-step guide revealed one at a time
}

export interface HandsOnActivity {
  title: string;
  materials: string[];
  instructions: string[];
  timeMinutes: number;
  whatToLookFor: string;
}

export interface WorksheetSection {
  type: 'warmup' | 'practice' | 'challenge' | 'creative' | 'reflection' |
        'fill-in' | 'draw' | 'calculate' | 'label' | 'sequence' | 'analyse' |
        'evaluate' | 'write' | 'classify' | 'match' | 'compare' | 'design' |
        'grid' | 'table' | 'choose' | 'convert' | 'decode' | 'plan' | 'trace' |
        'diagram' | 'interpret' | 'critique' | 'map' | 'system' | 'data';
  title: string;
  instructions?: string;
  content: string;
}

export interface PrintableWorksheet {
  title: string;
  sections: WorksheetSection[];
}

export interface ParentGuide {
  whatChildShouldKnow: string;
  commonMisconceptions: string;
  dinnerTableQuestion: string;
  nextLessonPreview: string;
}

export interface Lesson {
  id: string;
  pathwayId: string;
  subject: Subject;
  strand: string;
  ageBand: AgeBand;
  phase: Phase;
  level: number;
  language: 'no' | 'en';
  title: string;
  titleEn?: string;
  durationMinutes: number;
  saganHook: SaganHook;
  saganHookEn?: string;
  realWorldBridge: RealWorldBridge;
  realWorldBridgeEn?: RealWorldBridge;
  coreConcept: CoreConcept;
  coreConceptEn?: CoreConcept;
  interactiveComponent: InteractiveComponent;
  practiceQuestions: PracticeQuestion[];
  practiceQuestionsEn?: PracticeQuestion[];
  handsOnActivity: HandsOnActivity;
  handsOnActivityEn?: HandsOnActivity;
  printableWorksheet: PrintableWorksheet;
  printableWorksheetEn?: PrintableWorksheet;
  parentGuide: ParentGuide;
  parentGuideEn?: ParentGuide;
  openDoor: string;
  openDoorEn?: string;
  curriculumSources: string[];
  learningObjectives: string[];
  learningObjectivesEn?: string[];
  generatedAt: Date;
  version: number;
  source?: 'seed' | 'bonus';
  generatedBy?: string;
  /** Official LK20 competence goal this lesson was generated from (bonus lessons). */
  kompetansemaal?: { kode: string; text: string };
  lessonFormat?: 'story' | 'experiment' | 'mystery' | 'build';
}

// ─── Achievements ────────────────────────────────────────────────────────────

export interface EarnedBadge {
  id: string;
  earnedAt: Date;
}

export interface EarnedLessonBadge {
  lessonId: string;
  earnedAt: Date;
}

// ─── UI State ────────────────────────────────────────────────────────────────

export type LessonSection = 'hook' | 'bridge' | 'build' | 'practice' | 'activity';

export interface LessonState {
  lesson: Lesson | null;
  loading: boolean;
  error: string | null;
  currentSection: LessonSection;
  practiceAnswers: Record<number, string>;
  practiceResults: Record<number, boolean>;
  completed: boolean;
}
